import { assertIsTruthy, Variation } from '@llx/common';
import { Slide } from '@llx/common';
import { useMemo } from 'react';

import { AnswerFieldsFragment, ContextType, useListAnswersQuery } from '@/@generated/codegen/API';
import { getAnswerContextRelationWhereUnique } from '@/utils/getAnswerContextRelationwhereUnique';

function getFirstValidVariation(
  variations: Variation[],
  userAnswersToQuestions: AnswerFieldsFragment[]
): Variation | null {
  for (const variation of variations) {
    //get the question, check if the user answer is the one we want
    const userAnswers = userAnswersToQuestions.filter(
      (el) => el.slideId === variation.conditionKey
    );
    if (userAnswers.length === 0) {
      // No answers for this question, skip to next variation
      continue;
    }
    //NOTE MULTIVOTE: If at some point we want to add conditions according to multivote, we'll have to add a check here
    if (userAnswers[0].answerIndexes?.at(0) === variation.conditionValue) {
      return variation;
    }
  }
  return null;
}

export type UseSlideVariationReturnType = {
  slideHasVariations: boolean;
  isLoading: boolean;
  slideVariation: Variation | null | undefined;
};

export default function useSlideVariations(
  contextType: ContextType,
  objId: string | null,
  currentPatientCaseId: string | null | undefined,
  forceDisable: boolean,
  slide: Slide | null,
  allVariations: Variation[],
  userID: string | null
): UseSlideVariationReturnType {
  const slideHasVariations = (slide?.variationIds?.length || 0) > 0;

  const enabled =
    !!userID &&
    !forceDisable &&
    !!objId &&
    !!currentPatientCaseId &&
    !!slide &&
    slideHasVariations &&
    allVariations.length > 0;

  const userAnswersToQuestions = useListAnswersQuery({
    variables: {
      where: {
        ...(objId ? getAnswerContextRelationWhereUnique(contextType, objId) : {}),
        userId: {
          equals: userID,
        },
      },
      // TODO: test if this is what we want (goal: latest answer first that way even for
      // masterclass where multiple answers are possible we get the latest one&)
      orderByArgs: {
        direction: 'desc',
        orderByField: 'createdAt',
      },
    },
    skip: !enabled,
  });

  const slideVariation = useMemo(() => {
    if (!enabled || !userAnswersToQuestions.data) return null;
    assertIsTruthy(userID);
    assertIsTruthy(currentPatientCaseId);
    assertIsTruthy(slide);
    const possibleVariations = allVariations.filter((variation) =>
      slide.variationIds.includes(variation.variationId)
    );
    const firstValidVariation = getFirstValidVariation(
      possibleVariations,
      userAnswersToQuestions.data.listAnswers.edges.map((edge) => edge.node)
    );
    return firstValidVariation;
  }, [userAnswersToQuestions.data]);

  if (forceDisable || !slide || !slideHasVariations) {
    return {
      slideHasVariations: false,
      isLoading: false,
      slideVariation: null,
    };
  }

  return {
    slideHasVariations,
    isLoading: userAnswersToQuestions.loading,
    slideVariation: slideVariation,
  };
}
